import { Circle } from "@eisox/design-system";
import { SunIcon, TapIcon, TargetIcon, ThermometerIcon, ValveIcon, WaterDropIcon, WindIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { getAirQualityThreshold, getAirQualityV2Threshold, VALVE_HARDWARE_VERSION_ENUM } from "@eisox/valves";

import {
  canViewRoomData,
  getAirQualityColor,
  getHumidityColor,
  getLightColor,
  getOpenningColor,
  getTemperatureColor,
  roomDataIcon,
  type RoomDataMap,
} from "~/features/room";
import { isValveHardwareIsUpper } from "~/helpers/valve.helper";
import { m } from "~/paraglide/messages";
import type { Permission } from "~/types/auth.type";
import type { EnhancedValve } from "~/types/valve.type";

import * as Sensor from "./sensor";

interface ValveTooltipContentProps {
  valve: EnhancedValve;
  roomData: RoomDataMap;
  permissions: Permission;
  variant?: "tooltip" | "panel";
}

export const ValveTooltipContent: React.FC<ValveTooltipContentProps> = ({
  valve,
  roomData,
  permissions,
  variant = "tooltip",
}) => {
  const { sensors, closing, correctedTemp, hardwareVersion } = valve;
  const { temperature, humidity, indoorAirQuality, light } = sensors ?? {};

  const isValveV5 = isValveHardwareIsUpper(hardwareVersion, VALVE_HARDWARE_VERSION_ENUM.V5);

  const temperatureColor = temperature !== undefined ? getTemperatureColor(temperature) : undefined;
  const humidityColor = humidity !== undefined ? getHumidityColor(humidity) : undefined;

  const airQualityColor = indoorAirQuality !== undefined ? getAirQualityColor(indoorAirQuality, isValveV5) : undefined;
  const airQualityThreshold =
    indoorAirQuality !== undefined
      ? isValveV5
        ? getAirQualityV2Threshold(indoorAirQuality)
        : getAirQualityThreshold(indoorAirQuality)
      : undefined;

  const lightColor = light !== undefined ? getLightColor(light) : undefined;
  const openingColor = closing !== undefined ? getOpenningColor(100 - closing) : undefined;

  const correctedTempColor = correctedTemp
    ? { background: colors.primary.gray, color: colors.primary.darkBlue }
    : undefined;

  const isPanel = variant === "panel";

  return (
    <div className={isPanel ? "gap-5 flex flex-col" : "flex flex-col gap-[20px]"}>
      <div
        className={
          isPanel
            ? "md:grid-rows-[1fr_1fr] md:gap-x-10 md:gap-y-5 gap-x-2.5 gap-y-2.5 grid grid-flow-col grid-rows-[1fr_1fr_1fr]"
            : "md:grid-rows-[1fr_1fr] md:gap-x-[40px] md:gap-y-[20px] grid grid-flow-col grid-rows-[1fr_1fr_1fr] gap-x-[10px] gap-y-[10px]"
        }
      >
        <Sensor.Root>
          <Sensor.Icon style={{ backgroundColor: temperatureColor?.background, color: temperatureColor?.color }}>
            <ThermometerIcon />
            <Circle.Badge
              size={22}
              style={{ backgroundColor: temperatureColor?.background, color: temperatureColor?.color }}
            >
              <ValveIcon />
            </Circle.Badge>
          </Sensor.Icon>
          <Sensor.Value className={isPanel ? "text-primary-dark-gray" : undefined}>
            {temperature !== undefined ? temperature.toFixed(1) : "--"} <Sensor.Unity>°C</Sensor.Unity>
          </Sensor.Value>
        </Sensor.Root>
        {permissions.valve?.sensors?.humidity?.read && (
          <Sensor.Root>
            <Sensor.Icon style={{ backgroundColor: humidityColor?.background, color: humidityColor?.color }}>
              <WaterDropIcon />
            </Sensor.Icon>
            <Sensor.Value className={isPanel ? "text-primary-dark-gray" : undefined}>
              {humidity !== undefined ? humidity.toFixed(1) : "--"} <Sensor.Unity>%</Sensor.Unity>
            </Sensor.Value>
          </Sensor.Root>
        )}
        {permissions.valve?.sensors?.indoorAirQuality?.read && indoorAirQuality !== undefined && (
          <Sensor.Root>
            <Sensor.Icon style={{ backgroundColor: airQualityColor?.background, color: airQualityColor?.color }}>
              <WindIcon />
            </Sensor.Icon>
            <Sensor.Value className={isPanel ? "text-primary-dark-gray" : undefined}>
              {airQualityThreshold ? m.air_quality_values({ quality: airQualityThreshold }) : "--"}
            </Sensor.Value>
          </Sensor.Root>
        )}
        {permissions.valve?.sensors?.light?.read && (
          <Sensor.Root>
            <Sensor.Icon style={{ backgroundColor: lightColor?.background, color: lightColor?.color }}>
              <SunIcon />
            </Sensor.Icon>
            <Sensor.Value className={isPanel ? "text-primary-dark-gray" : undefined}>
              {light !== undefined ? light.toFixed(1) : "--"} <Sensor.Unity>lux</Sensor.Unity>
            </Sensor.Value>
          </Sensor.Root>
        )}
        {permissions.valve?.closing?.read && (
          <Sensor.Root>
            <Sensor.Icon style={{ backgroundColor: openingColor?.background, color: openingColor?.color }}>
              <TapIcon />
            </Sensor.Icon>
            <Sensor.Value className={isPanel ? "text-primary-dark-gray" : undefined}>
              {closing !== undefined ? (100 - closing).toFixed(1) : "--"} <Sensor.Unity>%</Sensor.Unity>
            </Sensor.Value>
          </Sensor.Root>
        )}
        {permissions.valve?.correctedTemp?.read && (
          <Sensor.Root>
            <Sensor.Icon style={{ backgroundColor: correctedTempColor?.background, color: correctedTempColor?.color }}>
              <TargetIcon />
            </Sensor.Icon>
            <Sensor.Value className={isPanel ? "text-primary-dark-gray" : undefined}>
              {correctedTemp !== undefined ? correctedTemp.toFixed(1) : "--"} <Sensor.Unity>°C</Sensor.Unity>
            </Sensor.Value>
          </Sensor.Root>
        )}
      </div>

      {Object.values(roomData).some(rd => rd.active && !rd.disabled) && canViewRoomData(permissions) && (
        <div className="gap-2.5 flex items-center">
          {Object.entries(roomData).map(([key, value]) => {
            if (!value.active || value.disabled) return null;
            return (
              <Circle.Root key={key} backgroundColor="blue" color="white">
                {roomDataIcon[key as keyof typeof roomDataIcon]}
              </Circle.Root>
            );
          })}
        </div>
      )}
    </div>
  );
};

ValveTooltipContent.displayName = "ValveTooltipContent";
